import { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import IosShareIcon from "@mui/icons-material/IosShare";
import Image from "next/image";
import { useRouter } from "next/router";

import SelectArtistImage from "@musicfy/assets/images/select_artist.png";
import { Features } from "@musicfy/contants/Features";
import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import { canUseFeature } from "@musicfy/utils";
import useIsMobile from "@musicfy/utils/hooks/useIsMobile";

import OnboardingStep from "./OnboardingStep";
import SelectArtistsTakeover from "./SelectArtistsTakeover";
import {
  ModelCategory,
  type OnboardingAction,
  type OnboardingState,
  type TModelCategory,
} from "./types";
import AudioPlayer from "../AudioPlayer";
import Button from "../Button";
import ModelThumbnail from "../ModelThumbnail";

const getSingleQueryParam = (
  value: string | string[] | undefined
): string | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

const isModelCategory = (value: string): value is TModelCategory =>
  (Object.values(ModelCategory) as string[]).includes(value);

interface SelectedModelsProps {
  selectedModels: IModel[];
  setSelectedModels: React.Dispatch<React.SetStateAction<IModel[]>>;
  dispatchOnboarding: React.Dispatch<OnboardingAction>;
  onboardingState: OnboardingState;
}

const SelectModel = ({
  selectedModels,
  setSelectedModels,
  dispatchOnboarding,
  onboardingState,
}: SelectedModelsProps): JSX.Element | null => {
  const { sendToastMessage } = useToastContext();
  const { subscription } = useSubscriptionContext();

  const router = useRouter();

  const [isTakeoverOpen, setIsTakeoverOpen] = useState(false);
  const [modelsCategoryFromUrl, setModelsCategoryFromUrl] = useState<
    TModelCategory | undefined
  >(undefined);
  // Callout on the + button after Convert Voice nav (same chrome as onboarding).
  const [showSelectVoiceTip, setShowSelectVoiceTip] = useState(false);
  const isMobile = useIsMobile();

  const model = selectedModels[0];

  const openVoicePicker = useCallback(() => {
    setShowSelectVoiceTip(false);
    setIsTakeoverOpen(true);
  }, []);

  useEffect(() => {
    if (selectedModels.length === 1 && onboardingState.currentStepIndex === 0) {
      dispatchOnboarding({ type: "NEXT_STEP" });
    }
  }, [
    dispatchOnboarding,
    onboardingState.currentStepIndex,
    selectedModels.length,
  ]);

  const onShareClick = useCallback(() => {
    if (!model) {
      return;
    }
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl);
    sendToastMessage("Link copied to clipboard", {
      severity: "info",
    });
  }, [model, sendToastMessage]);

  // open_takeover is a one-shot: open/close the picker then strip it.
  // Keep models_category in the URL so left-nav highlight can distinguish
  // Convert Voice vs Copyright-Free vs Instrumentals on the same page.
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const openTakeover = getSingleQueryParam(router.query.open_takeover);
    const modelsCategory = getSingleQueryParam(router.query.models_category);
    const modelsTab = getSingleQueryParam(router.query.models_tab);

    const shouldCloseTakeover = openTakeover === "false";
    const shouldOpenTakeover = openTakeover === "true";
    const validCategory =
      modelsCategory && isModelCategory(modelsCategory)
        ? modelsCategory
        : undefined;

    // Category-only URL (after open_takeover was stripped): sync picker
    // category for nav highlight, do not rewrite the query again.
    if (!shouldCloseTakeover && !shouldOpenTakeover && !modelsTab) {
      if (validCategory) {
        setModelsCategoryFromUrl(validCategory);
      }
      return;
    }

    if (shouldCloseTakeover) {
      setIsTakeoverOpen(false);
      setModelsCategoryFromUrl(undefined);
      setShowSelectVoiceTip(true);
    } else {
      if (shouldOpenTakeover) {
        setShowSelectVoiceTip(false);
        setIsTakeoverOpen(true);
      }

      if (validCategory) {
        setModelsCategoryFromUrl(validCategory);
      }
    }

    const nextQuery = { ...router.query };
    delete nextQuery.open_takeover;
    delete nextQuery.models_tab;
    // Convert Voice clears category so nav falls back to that item.
    if (shouldCloseTakeover) {
      delete nextQuery.models_category;
    }

    void router.replace(
      {
        pathname: router.pathname,
        query: nextQuery,
      },
      undefined,
      { shallow: true }
    );
  }, [
    router,
    router.isReady,
    router.pathname,
    router.query.open_takeover,
    router.query.models_category,
    router.query.models_tab,
  ]);

  const canSelectMultipleModels = canUseFeature(
    Features.SELECT_MULTIPLE_MODELS,
    subscription
  );

  return (
    <>
      <div className="w-full relative rounded-12">
        {!!model ? (
          <>
            <div className="relative overflow-hidden z-10 lg:px-9 px-4 py-6 gap-2 flex items-start justify-between h-full">
              <div className="lg:w-full w-1/2">
                <div className="flex items-center gap-4">
                  <div className="font-bold lg:text-38 text-26 break-words">
                    {model.artist}
                  </div>
                </div>
                <div className="mt-2 text-14 break-words">
                  {selectedModels.length === 1
                    ? `By ${model.createdBy || "Musicfy"}`
                    : selectedModels
                        .map((m) => m.artist)
                        .splice(1, selectedModels.length - 1)
                        .join(" | ")}
                </div>
                <div className="flex items-center gap-4 mt-6">
                  <Button
                    className="!py-[9px] whitespace-nowrap"
                    onClick={openVoicePicker}
                  >
                    {canSelectMultipleModels ? "Add Voice" : "Switch Voice"}
                  </Button>
                  {!isMobile ? (
                    <Button
                      onClick={onShareClick}
                      variant="outlined"
                      className="!py-2 !outline-white/15 !bg-white/10"
                    >
                      Share
                    </Button>
                  ) : (
                    <Button
                      onClick={onShareClick}
                      variant="outlined"
                      className="!py-2 !px-3 !outline-white/15 !bg-white/10"
                    >
                      <IosShareIcon />
                    </Button>
                  )}
                </div>
              </div>
              <div className="lg:w-44 w-32 aspect-square relative rounded-12 overflow-hidden">
                <div className="absolute bottom-2 left-2 z-10">
                  {model.audioPreviewUrl && (
                    <AudioPlayer
                      audioSrc={model.audioPreviewUrl}
                      type="circle"
                    />
                  )}
                </div>
                <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-b from-black/50 via-black/20 to-black/50" />
                <ModelThumbnail
                  thumbnail={model.thumbnail}
                  alt={`${model.artist} thumbnail`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="absolute left-0 top-0 w-full h-full rounded-12 overflow-hidden">
              <ModelThumbnail
                thumbnail={model.thumbnail}
                alt={`${model.artist} thumbnail`}
                className="h-full w-full absolute left-0 top-0 object-cover blur-3xl z-0"
              />
              <div className="absolute w-full h-full bg-gradient-to-r from-black/40 via-black/20 to-black-40 z-0" />
            </div>
          </>
        ) : (
          <>
            <div className="absolute w-full h-full rounded-12 left-0 top-0 overflow-hidden">
              <div className="bg-[#715450]/30 backdrop-blur top-0 left-0 w-full h-full absolute z-10" />
              <Image
                src={SelectArtistImage}
                alt="Select an artist image"
                className="blur-2xl object-cover w-full h-full"
              />
            </div>
            <div className="relative rounded-12 z-30 flex h-full gap-4 sm:gap-6 lg:gap-8 p-4 sm:p-6 items-center">
              <OnboardingStep
                onClose={() => {
                  setShowSelectVoiceTip(false);
                  dispatchOnboarding({ type: "CLOSE_ONBOARDING" });
                }}
                isOpen={
                  showSelectVoiceTip ||
                  (onboardingState.isOpen &&
                    onboardingState.currentStepIndex === 0)
                }
                content="Select a voice"
              >
                <div
                  className="border-dashed border-2 border-white/50 transition-all xl:hover:border-white/90 flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 shrink-0 aspect-square rounded-12"
                  role="button"
                  tabIndex={0}
                  aria-label="Select a voice"
                  onClick={openVoicePicker}
                >
                  <AddIcon fontSize="large" />
                </div>
              </OnboardingStep>
              <div className="min-w-0 flex-1 relative z-40 lg:pr-[38%] xl:pr-[34%]">
                <div className="text-24 sm:text-28 lg:text-32 font-semibold">
                  Select a voice
                </div>
                <div className="mt-2 font-light text-14 sm:text-16">
                  Start with <span className="text-green">copyright-free</span>{" "}
                  voices — safe for commercial use
                </div>
              </div>
            </div>
            <Image
              src={SelectArtistImage}
              alt="Select an artist image"
              className="absolute z-30 -right-4 bottom-0 h-[115%] w-auto lg:block hidden pointer-events-none"
            />
          </>
        )}
      </div>
      <SelectArtistsTakeover
        selectedModels={selectedModels}
        setSelectedModels={setSelectedModels}
        isOpen={isTakeoverOpen}
        onClose={() => setIsTakeoverOpen(false)}
        selectedLimit={4}
        modelsCategoryFromUrl={modelsCategoryFromUrl}
      />
    </>
  );
};

export default SelectModel;
